import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import TextFieldWrapper from '../../../../components/TextFieldWrapper/TextFieldWrapper';
import { confirmAlert } from '../../../../../services/mini/alanConfirmAlert';
import ConfirmPopup from '../../../../components/ConfirmPopup/ConfirmPopup';
import capitalize from '../../../../../services/mini/capitalize';
import { Loader } from '../../../../components/Loader/Loader';
import { BILLING_PAYPAL } from '../../../../../const/billingTypes';
import billingEvents from '../../../../../const/billingEvents';
import GaService from '../../../../../services/GaService';
import { BillingAmounts } from '../../BillingAmounts/BillingAmounts';
import { getDefaultAmount } from '../../getDefaultAmount';

class PayPalForm extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            amount: getDefaultAmount(this.props.amount),
            amountFieldError: null,
            amountFieldInfo: null,
            valid: true,
            payingIsInProcess: false
        };

        this.maxFieldLength = 500;
        this.onTextInputChange = this.onTextInputChange.bind(this);
        this.onTextInputBlur = this.onTextInputBlur.bind(this);
        this.onLengthValidate = this.onLengthValidate.bind(this);
    }

    componentDidMount() {
        const {
            createPaypalOrder,
            checkIsPaypalOrderApproved,
            onRendered,
            campaignName
        } = this.props;

        const {
            trackPaymentError = _.noop(),
            trackPaymentSuccess = _.noop(),
        } = this.context;

        if (document.getElementById('paypal-button-container') && window.paypal) {
            try {

                paypal.Buttons({
                    style: {
                        height: 52,
                        size: 'responsive',
                        label: 'pay',
                        layout: 'horizontal'
                    },
                    createOrder: (data, actions) => {
                        this.setState({ payingIsInProcess: true });
                        const { amount } = this.state;

                        this.trackPayment(billingEvents.paymentBtnClicked, +amount);
                        return createPaypalOrder({ amount: +amount }).then((orderId) => {
                            return orderId;
                        }, (createPaypalOrderErr) => {
                            trackPaymentError(this.getTrackInfo(billingEvents.paypalCreateOrderError + JSON.stringify(createPaypalOrderErr)));
                        });
                    },
                    onApprove: (data, actions) => {
                        return checkIsPaypalOrderApproved({ orderId: data.orderID }).then((isApproved) => {
                            if (isApproved) {
                                const { amount } = this.state;
                                this.showSuccessfulPaymentAlert();
                                GaService.sendPaymentEvent({
                                    amount,
                                    paymentType: BILLING_PAYPAL,
                                    campaignName,
                                });
                                trackPaymentSuccess({ ...this.getTrackInfo(), paymentAmount: +this.state.amount });
                            }
                        }, (checkIsPaypalOrderApprovedErr) => {
                            trackPaymentError(this.getTrackInfo(billingEvents.paypalOrderApprovedError + JSON.stringify(checkIsPaypalOrderApprovedErr)));
                        });
                    },
                    onCancel: (data, actions) => {
                        this.setState({ payingIsInProcess: false });
                        trackPaymentError(this.getTrackInfo(billingEvents.paypalWindowClosedByUser));
                    },
                    onError: (err) => {
                        trackPaymentError(this.getTrackInfo(billingEvents.paypalUnknownError + err));
                    }
                }).render('#paypal-button-container').then(() => {
                    const paypalContainer = document.getElementById('paypal-button-container');
                    if (paypalContainer) {
                        onRendered(paypalContainer.clientHeight);
                    }
                }).catch(() => { });
            }
            catch (globalPaypalErr) {
                trackPaymentError(this.getTrackInfo(billingEvents.paypalUnknownError + globalPaypalErr));
            }
        }
    }

    getTrackInfo(status, amount = 0) {
        const {
            isInitialBilling,
            contentId,
            contentVersion
        } = this.context;

        return {
            paymentMethod: BILLING_PAYPAL,
            isInitialBilling: isInitialBilling,
            contentId,
            contentVersion,
            paymentStatus: status,
            paymentAmount: amount
        };
    }

    trackPayment(status, amount) {
        const {
            trackPaymentStart = _.noop(),
        } = this.context;

        trackPaymentStart(this.getTrackInfo(status, amount));
    }

    showSuccessfulPaymentAlert() {
        const {
            initialPayment,
            updateUnloadPageListenerForBilling
        } = this.props;

        if (updateUnloadPageListenerForBilling) {
            updateUnloadPageListenerForBilling({ hasListener: false });
        }

        this.setState({ payingIsInProcess: false });

        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={() => {
                            onClose();
                            if (this.props.onBillingFinishedCb) {
                                this.props.onBillingFinishedCb();
                            }
                        }}
                        hideCancelBtn={true}
                        okBtnCb={() => {
                            onClose();
                            if (this.props.onBillingFinishedCb) {
                                this.props.onBillingFinishedCb();
                            }
                        }}
                        type="success"
                        title="Balance changed"
                        message={<React.Fragment>Your account has been replenished successfully. <br />
                            <b>Now your balance is ${Number(this.props.balance).toFixed(2)}.</b>
                        </React.Fragment>}
                    />
                );
            }
        });
    }

    validate(validationType) {
        let amountFieldError = null;
        let amountFieldInfo = null;
        let validatonRes;

        if (validationType === 'amount') {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength(validationType);
        }
        else {
            amountFieldError = this.validateAmount();
            amountFieldInfo = this.validateFieldLength('amount');
        }

        validatonRes = amountFieldError === null &&
            amountFieldInfo === null;


        this.setState({
            amountFieldError,
            amountFieldInfo,
            valid: validatonRes
        });

        return validatonRes;
    }

    validateAmount() {
        let { amount } = this.state;

        amount = +amount;

        if (isNaN(amount))
            return 'Amount is not valid';

        if (amount <= 0) {
            return 'Min amount is $1';
        }

        if (amount > 9999) {
            return 'Max amount is $9999';
        }
        return null;
    }

    validateField(field) {
        const fieldVal = this.state[field];
        let fieldError = null;

        if (!fieldVal) {
            if (field === 'name') {
                fieldError = 'Name on card should not be empty';
            } else {
                fieldError = capitalize(`${field} should not be empty`);
            }
        }

        return fieldError;
    }

    validateFieldLength(fieldType) {
        const field = this.state[fieldType];
        let fieldInfo = null,
            type;
        if (field.length > this.maxFieldLength) {
            if (!this.state[fieldType + 'FieldLength']) {
                this.setState({ [fieldType + 'FieldLength']: field.length });
            }
            if (fieldType === 'name') {
                type = 'name';
            }
            return `Max ${type} length: ${this.maxFieldLength} characters`;
        }

        this.setState({ [fieldType + 'FieldLength']: null });

        return fieldInfo;
    }

    onLengthValidate() {
        this.validate();
    }

    onTextInputChange(e) {
        const field = e.target.name;
        const fieldValue = e.target.value;
        const re = /^[0-9\b]+$/;
        let newState = this.state;

        if (field === 'amount') {
            if (fieldValue === '' || re.test(fieldValue)) {
                newState[field] = fieldValue;
            }
        } else {
            newState[field] = fieldValue;
        }

        this.setState(newState);
    }

    onTextInputBlur(validationType) {
        this.validate(validationType);
    }

    render() {
        const {
            amount,
            payingIsInProcess,
            amountFieldError,
            amountFieldInfo,
            valid
        } = this.state;

        return (
            <div className="paypal-form">
                {payingIsInProcess && <Loader theme="dark" size="medium" center={true} appearWithFade={true} />}
                <div className={`${payingIsInProcess && 'disabled'}`}>

                    <BillingAmounts amount={amount} onSelect={(newAmount) => {
                        this.setState({ amount: newAmount });
                    }} />

                    <div className={`popup-form_btns ${!valid && 'disabled'}`}>
                        <div id="paypal-button-container" style={{ width: '100%', textAlign: 'center' }}></div>
                    </div>
                    <div className="billing-forms_secure-info">
                        <i className="icon icon-alan-lock-small" />
                        Secure checkout. 30&#8288;-&#8288;day money&#8288;-&#8288;back guarantee.
                    </div>
                </div>
            </div>
        );
    }
}

PayPalForm.contextTypes = {
    trackPaymentStart: PropTypes.func,
    trackPaymentError: PropTypes.func,
    trackPaymentSuccess: PropTypes.func,
    isInitialBilling: PropTypes.bool,
    contentId: PropTypes.string,
    contentVersion: PropTypes.string,
};

export default PayPalForm;
